import { useEffect, useRef, useState } from 'react';
import { AssetType } from '@peritasai/types';

const TRACK_ASSET_PATH = '/find-asset';

type LocationLike = {
  pathname: string;
  search?: Record<string, unknown>;
};

type NavigateLike = (options: {
  to: string;
  search: { search?: string; type?: AssetType | undefined };
  replace?: boolean;
}) => void;

export const useTrackAssetSearch = ({
  location,
  navigate,
}: {
  location: LocationLike;
  navigate: NavigateLike;
}) => {
  const isTrackAssetRoute = location.pathname === TRACK_ASSET_PATH;
  const urlSearchParam = (location.search?.search as string | undefined) || '';
  const [searchValue, setSearchValue] = useState(() =>
    isTrackAssetRoute ? urlSearchParam : ''
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const lastUrlSearchRef = useRef<string>(urlSearchParam);
  const wasTrackAssetRouteRef = useRef<boolean>(isTrackAssetRoute);

  useEffect(() => {
    if (!isTrackAssetRoute) {
      return;
    }

    if (urlSearchParam !== lastUrlSearchRef.current) {
      setSearchValue(urlSearchParam);
    }
    lastUrlSearchRef.current = urlSearchParam;
  }, [isTrackAssetRoute, urlSearchParam]);

  useEffect(() => {
    const wasTrackAssetRoute = wasTrackAssetRouteRef.current;
    if (wasTrackAssetRoute && !isTrackAssetRoute) {
      setSearchValue('');
      lastUrlSearchRef.current = '';
    }
    wasTrackAssetRouteRef.current = isTrackAssetRoute;
  }, [isTrackAssetRoute]);

  const handleClearSearch = () => {
    setSearchValue('');
    setIsDropdownOpen(false);
    if (isTrackAssetRoute) {
      navigate({
        to: TRACK_ASSET_PATH,
        search: { search: undefined, type: undefined },
      });
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (value.trim().length > 0) {
      setIsDropdownOpen(false);
    }

    if (value.length === 0) {
      handleClearSearch();
    }
  };

  const handleSearchSubmit = () => {
    const trimmedValue = searchValue.trim();
    setIsDropdownOpen(false);
    if (trimmedValue.length === 0) {
      if (urlSearchParam.length > 0) {
        navigate({
          to: TRACK_ASSET_PATH,
          search: { search: undefined, type: location.search?.type as AssetType | undefined },
        });
      }
      return;
    }
    navigate({
      to: TRACK_ASSET_PATH,
      search: {
        search: trimmedValue.length > 0 ? trimmedValue : undefined,
        type: undefined,
      },
    });
  };



  const handleSearchFocus = () => {
    if (searchValue.trim().length === 0) {
      setIsDropdownOpen(true);
    }
  };

  const handleAssetTypeSelect = (type: AssetType) => {
    setIsDropdownOpen(false);
    navigate({
      to: TRACK_ASSET_PATH,
      search: { search: undefined, type },
    });
  };

  return {
    handleAssetTypeSelect,
    handleClearSearch,
    handleSearchChange,
    handleSearchFocus,
    handleSearchSubmit,
    isDropdownOpen,
    isTrackAssetRoute,
    searchValue,
    setIsDropdownOpen,
    urlSearchParam,
  };
};
